import React, { useMemo } from 'react';
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { get, upperFirst } from 'lodash';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useChargeQueries from '@dashboard/hooks/queries/useChargeQueries';
import useUrlState from '@dashboard/hooks/useUrlState';
import useTableSort from '@dashboard/hooks/useTableSort';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import ExportView from '@dashboard/components/ExportView';
import PageFilterInput from '@dashboard/components/Form/components/PageFilterInput';
import PageHeader from '@dashboard/components/PageHeader';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import FilterInput from '@dashboard/components/Form/components/FilterInput';
import StatusBadge from '@dashboard/components/StatusBadge';
import Tooltip from '@dashboard/components/Tooltip';
import Dash from '@dashboard/components/Dash';
import PaymentStatusBadge from '@dashboard/components/PaymentStatusBadge';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import { formatDateTime } from '@dashboard/lib/format-date';
import isRecoveredAbandonment from '@dashboard/services/abandonment/isRecoveredAbandonment';
import isAbandonedAbandonment from '@dashboard/services/abandonment/isAbandonedAbandonment';
import {
  BOOKING_FILTER_OPTIONS,
  BOOKINGS_LIST_ORDER_STATUS,
} from '@dashboard/lib/chargesConstants';
import { getStatusFromQuery } from '@dashboard/lib/tableHelpers';
import { orderIdColumn } from '@dashboard/lib/orderId';
import PageTitle from '@dashboard/components/PageTitle';
import { Badge } from '@dashboard/components/ui/badge';

function calculateTotalTickets(tickets) {
  return tickets.reduce((total, ticket) => total + ticket.quantity, 0);
}

const GUEST_DETAILS_BADGES = {
  missing: { status: 'unpaid', label: 'Missing details' },
  flagged: { status: 'unpaid', label: 'Review details' },
  complete: { status: 'hidden', label: 'Complete' },
};

export function getBookingColumns({ options = {} } = {}) {
  const columns = [orderIdColumn];

  if (!options.hideCustomer) {
    columns.push({
      accessorKey: 'customerEmail',
      header: ({ column }) => <DataGridColumnHeader column={column} title="Customer" />,
      cell: ({ row }) => {
        const payment = row.original;
        return (
          <div className="max-w-xs">
            <div className="font-medium">
              {payment.customerEmail || (payment.customer && get(payment, 'customer.email')) || (
                <Dash />
              )}
            </div>
            <div className="text-sm text-gray-500 dark:text-muted-foreground">
              {payment.customer && get(payment, 'customer.name')}
            </div>
          </div>
        );
      },
      enableSorting: true,
    });
  }

  columns.push({
    accessorKey: 'amount',
    header: ({ column }) => <DataGridColumnHeader column={column} title="Amount" />,
    cell: ({ row }) => {
      const payment = row.original;
      return (
        <span className="font-medium">
          {getPriceLabel({ amount: payment.amount, currency: payment.currency })}
        </span>
      );
    },
    enableSorting: true,
  });

  columns.push({
    id: 'tickets',
    header: 'Tickets',
    cell: ({ row }) => {
      const payment = row.original;
      if (!payment.tickets) return <Dash />;
      const canceled = payment.canceledTicketCount ?? 0;
      // A canceled booking keeps its count: its Canceled status already says the tickets are void.
      if (!canceled || payment.orderStatus === 'canceled')
        return calculateTotalTickets(payment.tickets);
      return (
        <span className="inline-flex items-center gap-2 whitespace-nowrap">
          {calculateTotalTickets(payment.tickets) - canceled}
          <Badge variant="destructive">{canceled} canceled</Badge>
        </span>
      );
    },
    enableSorting: false,
  });

  if (options.showGuestDetails) {
    columns.push({
      id: 'guestDetails',
      header: 'Attendee details',
      cell: ({ row }) => {
        const badge = GUEST_DETAILS_BADGES[row.original.attendeeStatus];
        return badge ? <StatusBadge {...badge} /> : <Dash />;
      },
      enableSorting: false,
    });
  }

  if (!options.hidePaymentLink) {
    columns.push({
      id: 'page',
      header: 'Event',
      cell: ({ row }) => (row.original.page && <PageTitle page={row.original.page} />) || <Dash />,
      enableSorting: false,
    });
  }

  columns.push({
    accessorKey: 'createdAt',
    header: ({ column }) => <DataGridColumnHeader column={column} title="Booked on" />,
    cell: ({ row }) => formatDateTime(row.original.createdAt),
    enableSorting: true,
  });

  if (!options.hideAbandonment) {
    columns.push({
      id: 'abandonment',
      header: 'Abandonment',
      cell: ({ row }) => {
        const payment = row.original;
        const isRecovered = isRecoveredAbandonment(payment);
        const isAbandoned = isAbandonedAbandonment(payment);

        if (isAbandoned) {
          return <PaymentStatusBadge status="abandoned" livemode={payment.livemode} />;
        }
        if (isRecovered) {
          return (
            <Tooltip label={`Recovered on ${formatDateTime(payment.recoveredAt)}`}>
              <StatusBadge status="recovered" label="Recovered" withInfoIcon />
            </Tooltip>
          );
        }
        return <Dash />;
      },
      enableSorting: false,
    });
  }

  if (!options.hideStatus) {
    columns.push({
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const payment = row.original;
        const isCanceled = payment.orderStatus === 'canceled';
        const isRefunded = payment.status === 'refunded' || payment.status === 'partial_refund';
        // PaymentStatusBadge collapses to a single "Test" badge off livemode, so a
        // second badge there would just repeat it.
        const complimentaryBadge =
          payment.isComplimentary && payment.livemode ? (
            <PaymentStatusBadge status="complimentary" livemode={payment.livemode} />
          ) : null;
        const archivedBadge =
          payment.archivedAt != null ? <StatusBadge status="archived" label="Archived" /> : null;

        if (payment.orderStatus === 'canceled' && isRefunded) {
          return (
            <div className="flex gap-1">
              <PaymentStatusBadge status={payment.orderStatus} livemode={payment.livemode} />
              <PaymentStatusBadge
                status={payment.status}
                livemode={payment.livemode}
                error={payment.paymentError}
              />
              {complimentaryBadge}
              {archivedBadge}
            </div>
          );
        }

        const statusBadge =
          isCanceled && !isRefunded ? (
            <PaymentStatusBadge status={payment.orderStatus} livemode={payment.livemode} />
          ) : (
            <PaymentStatusBadge
              status={payment.status}
              livemode={payment.livemode}
              error={payment.paymentError}
            />
          );

        if (complimentaryBadge || archivedBadge) {
          return (
            <div className="flex gap-1">
              {statusBadge}
              {complimentaryBadge}
              {archivedBadge}
            </div>
          );
        }

        return statusBadge;
      },
      enableSorting: false,
    });
  }

  return columns;
}

const PaymentsBookings = ({
  title = 'Event bookings',
  baseFilters = {},
  exportLabel = 'payments',
  hidePageFilter = false,
  embedded = false,
  filterOptions = BOOKING_FILTER_OPTIONS,
  hasCustomStatusFilter = false,
}) => {
  const [query, setParams] = useUrlState();
  const { sorting, onSortingChange, sort } = useTableSort();

  const search = query.search || '';
  const status = getStatusFromQuery(query.status, filterOptions);
  const page = query.page || undefined;

  const filter = {
    orderStatus: BOOKINGS_LIST_ORDER_STATUS,
    ...baseFilters,
    ...(filterOptions[status].value !== 'all' && {
      status: filterOptions[status].value,
    }),
    pageType: 'event',
    livemode: true,
    page: page || undefined,
  };

  const handleSearchChange = value => setParams({ search: value || undefined });

  const handleStatusChange = key =>
    setParams({
      status: filterOptions[key].value === 'all' ? undefined : filterOptions[key].value,
    });

  const handlePageChange = value => setParams({ page: value || undefined });

  const leftFilterControls = !hidePageFilter ? (
    <div className="w-full md:w-64">
      <PageFilterInput
        name="page"
        pageTypes={['event']}
        onChange={handlePageChange}
        initialValue={query.page}
        placeholder="Filter by event"
      />
    </div>
  ) : null;

  const filterControls = (
    <div className="flex flex-wrap items-center gap-2 max-md:w-full [&>*:first-child]:min-w-0 [&>*:first-child]:flex-1 lg:[&>*:first-child]:flex-none max-sm:[&>*:first-child]:basis-full">
      <SearchInput
        onChange={handleSearchChange}
        name="search_bookings"
        placeholder="Search by name, email or ID"
        initialValue={search}
        className="lg:w-80"
      />
      <FilterInput options={filterOptions} onChange={handleStatusChange} value={status} />
      <ExportView
        collection="payments"
        label={exportLabel}
        filters={{
          ...filter,
          sort: JSON.stringify(sort),
          search,
        }}
      />
    </div>
  );

  return (
    <div className={embedded ? 'space-y-4 px-4 pt-3 pb-4' : 'p-4 space-y-4'}>
      <PageHeader title={title} topBar={!embedded} />

      <PaymentsBookingsList
        sort={sort}
        search={search}
        filter={filter}
        hasCustomStatusFilter={hasCustomStatusFilter}
        filterControls={filterControls}
        leftFilterControls={leftFilterControls}
        sorting={sorting}
        onSortingChange={onSortingChange}
      />
    </div>
  );
};

export function PaymentsBookingsList({
  sort,
  search,
  filter = {},
  options = {},
  refreshKey,
  infinite = true,
  hasCustomStatusFilter = false,
  emptyStateTitle,
  emptyStateSubtitle,
  filterControls,
  leftFilterControls,
  sorting: sortingProp,
  onSortingChange,
}) {
  const { sellerId } = useAuth();
  const { getInfiniteCharges } = useChargeQueries();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, isLoading } =
    useInfiniteQuery({
      queryKey: ['charges', sellerId, sort, search, filter, refreshKey],
      placeholderData: keepPreviousData,
      queryFn: async ({ pageParam }) =>
        await getInfiniteCharges(sellerId, {
          ...filter,
          search,
          limit: 10,
          skip: pageParam * 10,
          sort: JSON.stringify(sort),
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!infinite) return undefined;
        if (!lastPage.has_more) return undefined;
        return allPages.length;
      },
    });

  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  // react-table mounts each `cell` function as a component: a new columns array remounts every cell.
  const { hideCustomer, hidePaymentLink, hideAbandonment, hideStatus, showGuestDetails } = options;
  const columns = useMemo(
    () =>
      getBookingColumns({
        options: { hideCustomer, hidePaymentLink, hideAbandonment, hideStatus, showGuestDetails },
      }),
    [hideCustomer, hidePaymentLink, hideAbandonment, hideStatus, showGuestDetails],
  );

  const isEmpty = !isLoading && flatData.length === 0;

  let emptyState;
  if (isEmpty && search) {
    emptyState = (
      <EmptyState>
        <EmptyState.Title>No bookings found</EmptyState.Title>
        <EmptyState.Subtitle>There aren't any results for this filter.</EmptyState.Subtitle>
      </EmptyState>
    );
  } else if (isEmpty && emptyStateTitle) {
    emptyState = (
      <EmptyState>
        {emptyStateTitle && <EmptyState.Title>{emptyStateTitle}</EmptyState.Title>}
        {emptyStateSubtitle && <EmptyState.Subtitle>{emptyStateSubtitle}</EmptyState.Subtitle>}
      </EmptyState>
    );
  } else if (isEmpty && filter.status && !hasCustomStatusFilter) {
    emptyState = (
      <EmptyState>
        <EmptyState.Title>No bookings yet</EmptyState.Title>
        <EmptyState.Subtitle>Your bookings will show up here.</EmptyState.Subtitle>
      </EmptyState>
    );
  } else if (isEmpty) {
    emptyState = (
      <EmptyState>
        <EmptyState.Title>No bookings yet</EmptyState.Title>
        <EmptyState.Subtitle>Your bookings will show up here.</EmptyState.Subtitle>
      </EmptyState>
    );
  }

  if (isError) {
    return <div className="m-4">Unable to show bookings. Please refresh or contact support.</div>;
  }

  return (
    <DataGrid
      columns={columns}
      data={flatData}
      isLoading={isLoading}
      paginationMode={infinite ? 'infinite' : undefined}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      sorting={sortingProp}
      onSortingChange={onSortingChange}
      toolbar={
        filterControls || leftFilterControls
          ? {
              leftActions: leftFilterControls,
              customActions: filterControls,
            }
          : undefined
      }
      getRowHref={payment => `/bookings/${payment._id}`}
      emptyState={emptyState}
      emptyMessage="No bookings found"
    />
  );
}

export default PaymentsBookings;
